import {Section, Provider} from './menu'
import {PermissionCondition, satisfyCondition} from 'admin/permissions/index'
import {differenceWith} from 'lodash-es'
import Logger from '@yandex-int/error-counter/dist/loggerWithEvents'
import config from 'common/config'

const logger = new Logger()

type Environment = 'development' | 'testing' | 'prestable' | 'production' | undefined

logger.initErrors({
  project: 'front-admin-sidebar',
  env: config.processEnv as Environment
})

type PossibleMatch = { data: Section[]; url: string }
const flow = (...fns: Function[]) => (x: any) => fns.reduce((y, f) => f(y), x)

const findAndOpenSections = (params: {
  strictUrlCheck: boolean;
  pathname: string;
  rootProvider?: Provider;
}) => (sections: Section[]): PossibleMatch[] => {
    /*
     * This function returns array of possible matches - max of one if strictUrlCheck enabled
     */
  const {strictUrlCheck, pathname, rootProvider} = params
  const result: PossibleMatch[] = []

  for (let i = 0; i < sections.length; i++) {
    const currentSection = sections[i]
    const rootProviderMatches = rootProvider
            ? rootProvider === currentSection.provider
            : true
    const url = currentSection.url || null
    const pathnameToUse =
            url && !strictUrlCheck ? pathname.slice(0, url.length) : pathname

    if (currentSection.sections) {
      const nestedResults = findAndOpenSections({
        strictUrlCheck,
        pathname,
        rootProvider
      })(currentSection.sections)

      nestedResults.map((nestedResult, index) => {
        const sectionsToPush = [...sections]
        sectionsToPush[i] = {
          ...sectionsToPush[i],
          isOpened: true,
          sections: nestedResult.data
        }
        result.push({
          url: nestedResults[index].url,
          data: sectionsToPush
        })
      })

      if (strictUrlCheck) {
        return result
      }
    } else if (url === pathnameToUse && rootProviderMatches) {
      const resultSections = [...sections]
      resultSections[i] = {...currentSection, isOpened: true}
      result.push({url: currentSection.url!, data: resultSections})

      if (strictUrlCheck) {
        return result
      }
    }
  }

  return result
}

const findSections = (initialParams: {
  strictUrlCheck: boolean;
  pathname: string;
  rootProvider?: Provider;
  sections: Section[];
}) => {
  const {strictUrlCheck, pathname, rootProvider, sections} = initialParams
  const matches = findAndOpenSections({
    strictUrlCheck,
    pathname,
    rootProvider
  })(sections)

  if (!matches.length) {
    return null
  }

  return matches.reduce((prev, current) =>
        prev.url.length > current.url.length ? prev : current
    ).data
}

export const openSections = (pathname: string, rootProvider?: Provider) => (
    inputSections: Section[]
) => {
  let openedSections = findSections({
    strictUrlCheck: true,
    pathname,
    rootProvider,
    sections: inputSections
  })

  if (!openedSections) {
    openedSections = findSections({
      strictUrlCheck: false,
      pathname,
      rootProvider,
      sections: inputSections
    })
  }

  return openedSections || inputSections
}

export const mergeSections = (defaultSections: Section[]) => (
    customUserSections: Section[] | null
) => {
    /*
     * This function merges sections saved in localStorage with default sections passed from yaml
     * Check out tests for examples
     */

  if (!customUserSections) {
    return defaultSections
  }

  const missingSections: Section[] = []

  defaultSections.forEach((defaultSection) => {
    const equivalentCustomUserSection = customUserSections.find(
      (localSection) => localSection.title === defaultSection.title &&
      defaultSection.url === localSection.url &&
      defaultSection.icon === localSection.icon
    )

    if (!equivalentCustomUserSection) {
      missingSections.push(defaultSection)
    }
  })

  const result: Section[] = customUserSections
        .map((localSection) => {
            const equivalentDefaultSection = defaultSections.find(
                (defaultSection) => defaultSection.title === localSection.title &&
                defaultSection.url === localSection.url &&
                defaultSection.icon === localSection.icon
            )

            if (!equivalentDefaultSection) {
                return false
            }

            const resultSections = {
                ...equivalentDefaultSection,
                url: equivalentDefaultSection.url || localSection.url,
                icon: equivalentDefaultSection.icon || localSection.icon,
                permissions:
                    equivalentDefaultSection.permissions ||
                    localSection.permissions,
                sections:
                    localSection.sections || equivalentDefaultSection.sections
                        ? mergeSections(
                              equivalentDefaultSection.sections || []
                          )(localSection.sections || [])
                        : []
            };

            (Object.keys(resultSections) as Array<keyof typeof resultSections>) // remove undefined section props
                .forEach((key) => {
                    if (resultSections[key] === undefined) {
                        delete resultSections[key]
                    }
                    if (
                        Array.isArray(resultSections[key]) &&
                        (resultSections[key] as []).length === 0
                    ) {
                        delete resultSections[key]
                    }
                })

            return resultSections
        })
        .filter((section) => section) as Section[]

  return [...result, ...missingSections]
}

type LogData = {
  title: string;
  icon?: string,
  url?: string;
  missedPermissions?: PermissionCondition[],
  additionally?: boolean
}

const logData: LogData[] = []

export const removeUnavailableSections = (permissions: string[]) => (sections: Section[]) => {
  const newSections: Section[] = []

  sections.forEach((section) => {
    if (section.sections) {
      const newNestedSections = removeUnavailableSections(permissions)(section.sections)
      if (newNestedSections.length) {
        newSections.push({...section, sections: newNestedSections})
      }
    } else {
      if (section.permissions) {
        const sectionPermissions = Array.isArray(section.permissions) ? section.permissions : [section.permissions]
        const matchedPermissions = sectionPermissions
          .filter((permission) => satisfyCondition(permission, permissions))

        const allPermissionsMatched = matchedPermissions.length === sectionPermissions.length

        if (allPermissionsMatched) {
          newSections.push(section)
        } else if (logData) {
          const logDataItem = {
            title: section.title,
            icon: section.icon,
            url: section.url,
            missedPermissions: sectionPermissions.filter((permission) => !matchedPermissions.includes(permission))
          }
          logData.push(logDataItem)
        }
      } else {
        newSections.push(section)
      }
    }
  })

  return newSections
}

const flattenSections = (sections: Section[], result: Section[] = []) => {
  for (const entry of sections) {
    result.push(entry)
    const newSections = entry.sections
    if (newSections && Array.isArray(newSections)) {
      flattenSections(newSections, result)
    }
  }

  return result
}

const compareSections = (a: Section, b: Section) => a.title === b.title && a.url === b.url

const addLogging = (defaultSections: Section[]) => (sections: Section[]) => {
  const flatDefaultSections = flattenSections([...defaultSections])
  const flatSections = flattenSections(sections)
  const removedSections = differenceWith(flatDefaultSections, flatSections, compareSections)

  // Нормально тут должны быть только разделы содержащие вложенные разделы и missedPermissions должно быть пустым
  const additionallyRemovedSections = differenceWith(removedSections, logData, compareSections).map((section) => ({
    title: section.title,
    url: section.url,
    icon: section.icon,
    missedPermissions: section.permissions,
    additionally: true
  }))
  logData.push(...additionallyRemovedSections)

  logger.logEventString('sections_removed', 'list_of_removed_sections', {
    additional: {
      sections: logData
    }
  })

  return sections
}

type InitialData = {
  defaultSections: Section[];
  customUserSections: Section[] | null;
  permissions: string[];
  pathname: string;
  rootProvider?: Provider;
}

export const initializeSections = (data: InitialData) =>
    flow(
      removeUnavailableSections(data.permissions),
      openSections(data.pathname, data.rootProvider),
      addLogging(data.defaultSections)
    )(data.defaultSections)
